/**
 * OTRIX Play — estimación de un entrenamiento y armado del plan para el motor de ejecución.
 *
 * OTRIX Play no guarda duración ni calorías por rutina, así que las derivamos aquí desde
 * series × repeticiones × descansos (una sola fuente para la card "Rutina de hoy" y el runner).
 * Marcamos siempre estos valores como "est." en la UI.
 */

import { DEFAULT_WEIGHT_KG, MET_REST, kcalFor, metFor } from 'components/play/metabolics';

// Constantes de estimación (comentadas para poder calibrarlas en un solo sitio).
export const SECS_PER_REP = 3; // tempo medio por repetición (concéntrica + excéntrica).
export const REST_BETWEEN_SETS = 75; // descanso entre series (s).
export const REST_BETWEEN_EXERCISES = 120; // descanso al cambiar de ejercicio (s).
// KCAL_PER_MIN quedó como FALLBACK de emergencia: el cálculo real es por ejercicio (MET,
// ver metabolics.js): una sentadilla no gasta lo mismo que un curl.
export const KCAL_PER_MIN = 7;

const DEFAULT_SETS = 3;
const DEFAULT_REPS = 10;

/**
 * Convierte "8-12", "10", "AMRAP" → un número de repeticiones para estimar (punto medio de rangos).
 */
export const parseReps = (repsStr, fallback = DEFAULT_REPS) => {
  if (repsStr === null || repsStr === undefined) return fallback;
  const nums = String(repsStr).match(/\d+/g);
  if (!nums || nums.length === 0) return fallback;
  if (nums.length === 1) return Number(nums[0]);
  const a = Number(nums[0]);
  const b = Number(nums[1]);
  return Math.round((a + b) / 2);
};

/**
 * Descanso entre series ajustado por el peso indicado (más peso → algo más de descanso).
 * `weight` es opcional; sin peso devuelve el descanso base.
 */
export const restForWeight = (weight, base = REST_BETWEEN_SETS) => {
  const w = Number(weight);
  if (!w || Number.isNaN(w) || w <= 0) return base;
  // +1s por kg por encima de 20kg, con tope en +45s.
  const extra = Math.min(45, Math.max(0, Math.round((w - 20) * 1)));
  return base + extra;
};

/**
 * Construye el plan de UN ejercicio de la rutina (sets/reps/descansos/segundos) para el runner.
 */
/**
 * Cómo se mide un ejercicio de Play: 'TIME_SECONDS' o 'REPS'.
 *
 * Sale de `parameter_types` (Tiempo / Repeticiones / Peso / Distancia), que es lo que el admin de
 * Play llena de forma OBLIGATORIA. NO de `benchmark_metric`: ese campo es de OTRIXVERSE y el
 * formulario de Play nunca lo envía, así que está NULL en todos los ejercicios Play.
 * Espeja `metricFor()` de PlayWorkoutMutation.php — si cambias uno, cambia el otro.
 */
export const metricOf = (exercise) => {
  const types = exercise?.parameter_types || [];
  return types.includes('TIME') && !types.includes('REPS') ? 'TIME_SECONDS' : 'REPS';
};

/**
 * Series efectivas según la semana del mesociclo (1..4). Espejo del modelo del backend:
 * la plantilla guarda los valores BASE y la progresión se aplica aquí, al armar el plan del día.
 *
 *  - Semana 1: base. Semana 2: +1 serie al PRIMER ejercicio del día (el compuesto principal).
 *  - Semana 3: +1 serie a los dos primeros. Semana 4: DESCARGA, mitad de series (gestión de
 *    fatiga estándar; se presenta honesta, no como requisito probado).
 *
 * El builder/editor NUNCA pasa semana: siempre muestra (y edita) los valores base.
 */
export const progressedSets = (sets, index, week) => {
  // Los ítems de 1 serie son finishers (cardio "15-20 min"): no progresan ni descargan.
  if (sets <= 1) return sets;
  if (week === 4) return Math.max(1, Math.ceil(sets * 0.5));
  if (week === 3 && index <= 1) return sets + 1;
  if (week === 2 && index === 0) return sets + 1;
  return sets;
};

export const buildExercisePlan = (item, { index = 0, week = 1 } = {}) => {
  const ex = item.exercise || {};
  const baseSets = Number(item.target_sets) || Number(ex.suggested_sets) || DEFAULT_SETS;
  const sets = progressedSets(baseSets, index, week);
  const repsStr = item.target_reps || ex.suggested_reps || String(DEFAULT_REPS);
  const reps = parseReps(repsStr);
  const metric = metricOf(ex);

  return {
    itemId: item.id,
    exerciseId: ex.id || item.exercise_id,
    name: ex.localized_name || ex.name || '',
    muscle: ex.muscle_group || null,
    image1: ex.image_url_1 || null,
    image2: ex.image_url_2 || null,
    // MET de trabajo del ejercicio (metabolics.js): alimenta las kcal reales del runner.
    met: metFor(ex),
    // Cómo se mide este ejercicio. El runner decide con esto si pide repeticiones o si le basta
    // el cronómetro. Sin esto el motor no sabía qué estaba midiendo.
    benchmarkMetric: metric,
    sets,
    reps,
    repsLabel: repsStr,
    // Medido por tiempo: el objetivo YA está en segundos ("40-60 s" → 50), no se multiplica
    // por el tempo de una repetición.
    secondsPerSet: metric === 'TIME_SECONDS' ? Math.max(10, reps) : Math.max(10, reps * SECS_PER_REP),
    // Descanso prescrito por el generador según objetivo y tipo (ACSM 2026); sin él, el default.
    restBetweenSets: Number(item.rest_seconds) || REST_BETWEEN_SETS,
    restBetweenExercises: REST_BETWEEN_EXERCISES,
    weights: new Array(sets).fill(null),
    // Lo REALMENTE hecho por serie (lo objetivo vive en `reps`/`repsLabel`).
    repsDone: new Array(sets).fill(null),
    secondsDone: new Array(sets).fill(null),
    // Descanso real tras cada serie y si se cortó muy por debajo del prescrito: es lo que
    // alimenta el aviso ACSM (restAdvice.js). El último índice es el cambio de ejercicio.
    restDone: new Array(sets).fill(null),
    restSkipped: new Array(sets).fill(false),
    setsDone: 0
  };
};

/**
 * Plan completo (lista de ejercicios) del día — el payload que consume `playWorkout.start`.
 * `week` (1..4, opcional) aplica la progresión del mesociclo; sin ella, valores base.
 */
export const buildWorkoutPlan = (items, week = 1) => (items || []).map((item, index) => buildExercisePlan(item, { index, week }));

/**
 * Estimación agregada de un día de rutina. `week` opcional: con ella la duración/series
 * reflejan la semana del mesociclo. `weightKg` opcional (perfil Play): afina las kcal.
 *
 * Calorías por EJERCICIO con base MET: trabajo al MET del ejercicio (tipo + músculo) y
 * descansos a MET de recuperación — ya no el plano 7 kcal/min para todo.
 */
export const estimateWorkout = (items, week = 1, weightKg = DEFAULT_WEIGHT_KG) => {
  const plan = buildWorkoutPlan(items, week);
  const n = plan.length;

  if (n === 0) {
    return { exerciseCount: 0, totalSets: 0, durationSec: 0, durationMin: 0, calories: 0 };
  }

  let durationSec = 0;
  let totalSets = 0;
  let calories = 0;

  plan.forEach((p) => {
    const workSec = p.sets * p.secondsPerSet;
    const restSec = Math.max(0, p.sets - 1) * p.restBetweenSets;
    totalSets += p.sets;
    durationSec += workSec + restSec;
    calories += kcalFor(workSec, p.met, weightKg) + kcalFor(restSec, MET_REST, weightKg);
  });
  const betweenSec = Math.max(0, n - 1) * REST_BETWEEN_EXERCISES; // descanso entre ejercicios
  durationSec += betweenSec;
  calories += kcalFor(betweenSec, MET_REST, weightKg);

  const durationMin = Math.max(1, Math.round(durationSec / 60));

  return {
    exerciseCount: n,
    totalSets,
    durationSec,
    durationMin,
    calories: Math.max(1, Math.round(calories))
  };
};

/* ------------------- Lectura de lo hecho (series reales) -------------------- */

/**
 * Series realmente hechas de un ejercicio, venga del historial (`details` del backend, snake_case)
 * o del plan vivo del runner (camelCase). Las dos formas conviven: el resumen del cierre lee el
 * plan y "Rendimientos" lee lo persistido.
 */
const doneValues = (item, snakeKey, camelKey) => {
  const values = item?.[snakeKey] || item?.[camelKey] || [];
  const setsDone = item?.sets_done ?? item?.setsDone ?? 0;
  return values.slice(0, setsDone).map((v) => Number(v));
};

/**
 * Reps de un ejercicio dentro de un entrenamiento: las REALES por serie ("10 · 8 · 6") si el
 * motor las capturó, y si no la etiqueta objetivo ("8-12") de las sesiones viejas.
 */
export const repsLabelOf = (item) => {
  const done = doneValues(item, 'reps_done', 'repsDone').filter((v) => v > 0);
  return done.length ? done.join(' · ') : item?.repsLabel || item?.reps || '';
};

/** Segundos cronometrados de cada serie ("45 · 40 · 38"); vacío si no se midieron. */
export const secondsLabelOf = (item) => doneValues(item, 'seconds_done', 'secondsDone').filter((v) => v > 0).join(' · ');

/** Tiempo total dedicado a ESE ejercicio (suma de sus series), 0 si no hay medición. */
export const secondsDoneTotal = (item) =>
  doneValues(item, 'seconds_done', 'secondsDone').reduce((acc, v) => acc + (v > 0 ? v : 0), 0);

/**
 * Formatea segundos a "m:ss" o "h:mm:ss".
 */
export const formatDuration = (totalSeconds) => {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  }
  return `${m}:${String(sec).padStart(2, '0')}`;
};
